"use client";

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * DigitalLcModal — popup that shows the audience a "digital LC"
 * document the moment the LC is issued. Renders the SWIFT MT 700
 * key fields as a styled card (no PDF dependency) so finance audiences
 * see what the bank actually sent on the wire.
 *
 * Closes on backdrop click / Esc / close button.
 */
export function DigitalLcModal({
  open,
  onClose,
  lcReference,
  amountUsd,
  expiry,
  issuingBank,
  advisingBank,
  confirmingBank,
  applicantName,
  beneficiaryName,
  presentationPeriodDays = 21,
  partialFinancingPct,
}: {
  open: boolean;
  onClose: () => void;
  lcReference: string;
  amountUsd: number;
  expiry: string;
  issuingBank: string;
  advisingBank?: string;
  /** When set, surfaces the :49: field with "Confirmed by …" — the
   *  confirming bank adds its own independent payment undertaking on
   *  top of the issuing bank's. Optional: most real LCs don't carry
   *  confirmation. */
  confirmingBank?: string;
  applicantName: string;
  beneficiaryName: string;
  presentationPeriodDays?: number;
  partialFinancingPct?: number;
}) {
  const t = useTranslations("digitalLc");
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  // Portal: escape the .scene-fade transformed ancestor so the modal
  // centres on the viewport (not the SceneRouter pane).
  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-orbitra-ink/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="digital-lc-title"
        className="glass relative flex w-[min(70vw,720px)] flex-col gap-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label={t("closeCta")}
          onClick={onClose}
          className="absolute end-3 top-2 text-[18px] leading-none text-orbitra-muted hover:text-orbitra-paper"
        >
          ×
        </button>

        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
              {t("titleKicker")}
            </p>
            <h3
              id="digital-lc-title"
              className="mt-1 font-display text-lg font-semibold text-orbitra-paper"
              dir="ltr"
            >
              {lcReference}
            </h3>
          </div>
          <p className="text-[10px] text-orbitra-muted">
            <span className="font-mono text-orbitra-paper">Esc</span> {t("escDismissPrefix")}
          </p>
        </div>

        <div className="rounded-md border border-state-financed/40 bg-orbitra-ink/40 p-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[12px]">
            <LcField tag="20"    label={t("labelCreditRef")}        value={lcReference} />
            <LcField tag="31D"   label={t("labelExpiry")}           value={expiry} />
            <LcField tag="32B"   label={t("labelAmount")}           value={`USD ${amountUsd.toLocaleString()}`} />
            <LcField tag="40A"   label={t("labelFormOfCredit")}     value={t("valueIrrevocable")} />
            <LcField tag="50"    label={t("labelApplicant")}        value={applicantName} />
            <LcField tag="59"    label={t("labelBeneficiary")}      value={beneficiaryName} />
            <LcField tag="52A"   label={t("labelIssuingBank")}      value={issuingBank} />
            {advisingBank && (
              <LcField tag="57A" label={t("labelAdvisingBank")}     value={advisingBank} />
            )}
            <LcField tag="41A"   label={t("labelAvailableWith")}    value={advisingBank ?? issuingBank} />
            <LcField tag="42C"   label={t("labelDraftsAt")}         value={t("valueSight")} />
            <LcField tag="43P"   label={t("labelPartialShipments")} value={t("valueNotAllowed")} />
            <LcField tag="43T"   label={t("labelTransshipment")}    value={t("valueNotAllowed")} />
            <LcField tag="48"    label={t("labelPresentationPeriod")} value={`${presentationPeriodDays} ${t("valueDaysFromShipmentSuffix")}`} />
            <LcField tag="71D"   label={t("labelCharges")}          value={t("valueChargesText")} />
            {partialFinancingPct !== undefined && (
              <LcField tag="78"  label={t("labelInstructions")}     value={`${partialFinancingPct}% ${t("valueBankFinancingSuffix")}`} />
            )}
            <LcField
              tag="49"
              label={t("labelConfirmation")}
              value={confirmingBank ? `${t("valueConfirmedByPrefix")} ${confirmingBank}` : t("valueConfirmationWithout")}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 text-[10px]">
          <div className="flex flex-col">
            <span className="font-mono uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("anchoredKicker")}
            </span>
            <span className="text-orbitra-paper">
              {t("anchoredDetail")}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="cta-primary px-5 py-2.5 font-display text-sm font-semibold"
          >
            {t("closeCta")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function LcField({ tag, label, value }: { tag: string; label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
        :{tag}: {label}
      </span>
      <span className="mt-0.5 text-orbitra-paper">{value}</span>
    </div>
  );
}
